/* Noor Mahal theme: everything around the hero. The light line, the lamp test, the invitation. */
(() => {
  'use strict';

  const DUST_COUNT = 22;
  const SETTLE_PAD_MS = 1200;          // after the last stagger step, delays retire
  const STAGGER_STEP_MS = 100;
  const NAV_SOLID_PX = 96;
  const THREAD_LEAD = 0.55;            // the line's tip rides at 55% of the viewport height
  const THREAD_STEP = 0.002;
  const LAMP_ANCHOR_MAX = 24;          // a lamp sits beside the first line of its element
  const DEFAULT_HOLD_MS = 1800;        // how long the lamp takes to rise
  const RELEASE_K = 0.06;              // how softly the light falls back when released early
  const HOLD_STEP = 0.004;
  const HOLD_FLOOR = 0.002;
  const FRAME_MS = 16.667;
  const LATTICE_STARS = 15;
  const LATTICE_GAP = 80;
  const LATTICE_START = 40;
  const LATTICE_Y = 32;
  const STAR_OUTER = 14;
  const STAR_INNER = STAR_OUTER * 0.7654;   // two overlapping squares make the eight-point star
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const changed = (next, prev, step) =>
    Math.abs(next - prev) >= step || ((next === 0 || next === 1) && next !== prev);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- The environment: whisper-level dust ---------- */

  function makeDust() {
    const host = document.getElementById('dust');
    if (!host || host.childElementCount) return;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < DUST_COUNT; i++) {
      const r = Math.random;
      const mote = document.createElement('span');
      mote.className = 'mote';
      mote.style.cssText = [
        `--x:${(r() * 100).toFixed(1)}%`,
        `--y:${(r() * 100).toFixed(1)}%`,
        `--s:${(1 + r() * 2).toFixed(1)}px`,
        `--d:${(26 + r() * 30).toFixed(1)}s`,
        `--g:${(5 + r() * 6).toFixed(1)}s`,
        `--dl:${(-r() * 50).toFixed(1)}s`,
        `--dl2:${(-r() * 10).toFixed(1)}s`,
        `--dx:${Math.round(r() * 40 - 20)}px`,
        `--o:${(0.25 + r() * 0.45).toFixed(2)}`
      ].join(';');
      frag.append(mote);
    }
    host.append(frag);
  }

  document.addEventListener('visibilitychange', () => {
    document.body.classList.toggle('paused', document.hidden);
  });

  /* ---------- The lattice divider, drawn by hand ---------- */

  function starPoints(cx) {
    const pts = [];
    for (let i = 0; i < 16; i++) {
      const angle = ((-90 + i * 22.5) * Math.PI) / 180;
      const radius = i % 2 ? STAR_INNER : STAR_OUTER;
      pts.push(`${(cx + radius * Math.cos(angle)).toFixed(2)},${(LATTICE_Y + radius * Math.sin(angle)).toFixed(2)}`);
    }
    return pts.join(' ');
  }

  function diamondPoints(cx) {
    const y = LATTICE_Y;
    return `${cx},${y - 4} ${cx + 4},${y} ${cx},${y + 4} ${cx - 4},${y}`;
  }

  function buildLattice(svg) {
    if (svg.childElementCount) return;
    const NS = 'http://www.w3.org/2000/svg';
    let index = 0;
    const add = (tag, attrs) => {
      const el = document.createElementNS(NS, tag);
      Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, String(value)));
      el.setAttribute('class', 'ln');
      el.setAttribute('pathLength', '1');
      el.style.setProperty('--i', String(index++));
      svg.append(el);
    };
    for (let s = 0; s < LATTICE_STARS; s++) {
      const cx = LATTICE_START + s * LATTICE_GAP;
      add('polygon', { points: starPoints(cx) });
      if (s === LATTICE_STARS - 1) continue;
      add('line', { x1: cx + STAR_OUTER, y1: LATTICE_Y, x2: cx + 36, y2: LATTICE_Y });
      add('polygon', { points: diamondPoints(cx + 40) });
      add('line', { x1: cx + 44, y1: LATTICE_Y, x2: cx + LATTICE_GAP - STAR_OUTER, y2: LATTICE_Y });
    }
  }

  /* ---------- Entrances and living elements ---------- */

  const entranceObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      el.classList.add('in');
      entranceObserver.unobserve(el);
      if (el.classList.contains('stagger')) {
        const wait = SETTLE_PAD_MS + el.children.length * STAGGER_STEP_MS;
        setTimeout(() => el.classList.add('settled'), wait);
      }
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });

  const livingObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => entry.target.classList.toggle('live', entry.isIntersecting));
  }, { rootMargin: '10% 0px' });

  function watchElements() {
    document.querySelectorAll('.stagger:not(.in), .reveal:not(.in), .draw:not(.in)')
      .forEach(el => entranceObserver.observe(el));
    document.querySelectorAll('.section, .invite, .thread').forEach(el => livingObserver.observe(el));
  }

  /* ---------- Nav: clear over the hero, solid everywhere else ---------- */

  const nav = document.getElementById('nav');
  let navSolid = null;

  function updateNav() {
    const hero = document.querySelector('.hero');
    if (!nav || !hero) return;
    const solid = hero.getBoundingClientRect().bottom < NAV_SOLID_PX;
    if (solid !== navSolid) {
      nav.classList.toggle('solid', solid);
      navSolid = solid;
    }
  }

  /* ---------- The light line: the signature thread ---------- */

  const main = document.getElementById('main');
  const thread = document.getElementById('thread');
  let lamps = [];
  let threadStart = 0;
  let threadLength = 0;
  let mainTop = 0;
  let threadShown = -1;
  let threadPinned = false;
  let pooled = null;

  function offsetWithin(el, ancestor) {
    let y = 0;
    let node = el;
    while (node && node !== ancestor) {
      y += node.offsetTop;
      node = node.offsetParent;
    }
    return y;
  }

  function layoutThread() {
    if (!main || !thread) return;
    const lampEls = [...main.querySelectorAll('[data-lamp]')];
    if (!lampEls.length) {
      thread.hidden = true;
      threadLength = 0;
      return;
    }
    thread.hidden = false;
    const hero = document.querySelector('.hero');
    mainTop = main.getBoundingClientRect().top + window.scrollY;
    threadStart = hero ? offsetWithin(hero, main) + hero.offsetHeight : 0;
    const ys = lampEls.map(el =>
      offsetWithin(el, main) + Math.min(el.offsetHeight / 2, LAMP_ANCHOR_MAX) - threadStart);
    threadLength = Math.max(1, ys[ys.length - 1]);
    thread.style.setProperty('--thread-top', `${Math.round(threadStart)}px`);
    thread.style.setProperty('--thread-h', `${Math.round(threadLength)}px`);
    if (lamps.length !== ys.length) {
      lamps.forEach(l => l.node.remove());
      lamps = ys.map(() => {
        const node = document.createElement('span');
        node.className = 'node';
        thread.append(node);
        return { node, y: 0, lit: false };
      });
    }
    lamps.forEach((lamp, i) => {
      lamp.y = ys[i];
      lamp.node.style.top = `${Math.round(ys[i])}px`;
    });
    threadShown = -1;
    pooled = null;
    updateThread();
  }

  function updateThread() {
    if (!thread || !threadLength) return;
    const tipY = window.scrollY + window.innerHeight * THREAD_LEAD - (mainTop + threadStart);
    const p = threadPinned ? 1 : clamp(tipY / threadLength, 0, 1);
    if (changed(p, threadShown, THREAD_STEP)) {
      thread.style.setProperty('--tp', p.toFixed(4));
      threadShown = p;
    }
    const tipPx = p * threadLength;
    lamps.forEach(lamp => {
      const lit = tipPx >= lamp.y - 1;
      if (lit !== lamp.lit) {
        lamp.node.classList.toggle('lit', lit);
        lamp.lit = lit;
      }
    });
    const pool = p >= 0.999;
    if (pool !== pooled) {
      thread.classList.toggle('pooled', pool);
      document.querySelectorAll('.invite').forEach(el => el.classList.toggle('pooled', pool));
      pooled = pool;
    }
  }

  let scrollQueued = false;
  function onScroll() {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(() => {
      scrollQueued = false;
      updateNav();
      updateThread();
    });
  }

  let layoutQueued = false;
  function scheduleLayout() {
    if (layoutQueued) return;
    layoutQueued = true;
    requestAnimationFrame(() => {
      layoutQueued = false;
      layoutThread();
    });
  }

  /* ---------- The one interactive moment: hold it to the lamp ---------- */

  function setupHold(cloth) {
    const button = cloth.querySelector('[data-hold]');
    const status = cloth.querySelector('[data-hold-status]');
    if (!button || !status || cloth.dataset.holdReady) return null;
    cloth.dataset.holdReady = 'true';

    const holdMs = Number(cloth.dataset.holdMs) || DEFAULT_HOLD_MS;
    const facts = [...cloth.querySelectorAll('.fact')].map(el => ({ el, at: Number(el.dataset.at), on: false }));
    const state = { hold: 0, shown: -1, holding: false, raf: null, last: 0, done: false, pinned: false };

    const render = () => {
      if (changed(state.hold, state.shown, HOLD_STEP)) {
        cloth.style.setProperty('--hold', state.hold.toFixed(3));
        state.shown = state.hold;
      }
      facts.forEach(fact => {
        const on = state.hold >= fact.at;
        if (on !== fact.on) {
          fact.el.classList.toggle('on', on);
          fact.on = on;
        }
      });
    };

    const complete = pinnedByMotionSetting => {
      const hadFocus = document.activeElement === button;
      state.done = true;
      state.holding = false;
      state.pinned = pinnedByMotionSetting;
      state.hold = 1;
      render();
      button.classList.remove('pressing');
      button.setAttribute('aria-hidden', 'true');
      button.tabIndex = -1;
      cloth.classList.add('done');
      status.textContent = status.dataset.doneText || '';
      if (hadFocus) status.focus({ preventScroll: true });
    };

    const reset = () => {
      state.done = false;
      state.pinned = false;
      state.hold = 0;
      render();
      button.removeAttribute('aria-hidden');
      button.tabIndex = 0;
      cloth.classList.remove('done');
      status.textContent = '';
    };

    const tick = now => {
      const dt = Math.min(100, now - (state.last || now));
      state.last = now;
      if (state.holding) {
        state.hold = Math.min(1, state.hold + dt / holdMs);
      } else {
        state.hold += (0 - state.hold) * (1 - Math.pow(1 - RELEASE_K, dt / FRAME_MS));
        if (state.hold < HOLD_FLOOR) state.hold = 0;
      }
      render();
      if (state.hold >= 1 && !state.done) complete(false);
      if (state.done || (!state.holding && state.hold === 0)) {
        state.raf = null;
        state.last = 0;
        return;
      }
      state.raf = requestAnimationFrame(tick);
    };

    const start = event => {
      if (state.done) return;
      event.preventDefault();
      state.holding = true;
      button.classList.add('pressing');
      if (state.raf === null) state.raf = requestAnimationFrame(tick);
    };

    const end = () => {
      if (!state.holding) return;
      state.holding = false;
      button.classList.remove('pressing');
      if (state.raf === null && !state.done) state.raf = requestAnimationFrame(tick);
    };

    button.addEventListener('pointerdown', event => {
      if (event.button !== 0) return;
      if (button.setPointerCapture) button.setPointerCapture(event.pointerId);
      start(event);
    });
    ['pointerup', 'pointercancel', 'lostpointercapture', 'blur'].forEach(type => button.addEventListener(type, end));
    button.addEventListener('keydown', event => {
      if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) start(event);
    });
    button.addEventListener('keyup', event => {
      if (event.key === ' ' || event.key === 'Enter') end();
    });
    button.addEventListener('contextmenu', event => event.preventDefault());
    render();

    return {
      pin: () => { if (!state.done) complete(true); },
      unpin: () => { if (state.pinned) reset(); }
    };
  }

  let holds = [];
  function wireHolds() {
    const fresh = [...document.querySelectorAll('.cloth')].map(setupHold).filter(Boolean);
    holds = holds.concat(fresh);
    if (reduceMotion.matches) fresh.forEach(h => h.pin());
  }

  /* ---------- Reduced motion, honored live in both directions ---------- */

  function pinToFinalStates() {
    threadPinned = true;
    threadShown = -1;
    updateThread();
    holds.forEach(h => h.pin());
  }

  function unpinFinalStates() {
    threadPinned = false;
    threadShown = -1;
    updateThread();
    holds.forEach(h => h.unpin());
  }

  reduceMotion.addEventListener('change', event => {
    if (event.matches) pinToFinalStates();
    else unpinFinalStates();
  });

  /* ---------- The invitation form: checked here, sent by Shopify to the store's inbox ---------- */

  function wireForm() {
    const posted = document.querySelector('[data-posted]');
    if (posted) {
      const target = posted.closest('.invite') || posted;
      target.scrollIntoView({ block: 'center' });
      posted.focus({ preventScroll: true });
    }

    const form = document.getElementById('invite-form');
    if (!form || form.dataset.ready) return;
    form.dataset.ready = 'true';
    const errorEl = form.querySelector('[data-form-error]');
    const pieceSelect = form.querySelector('select');
    const field = name => form.elements.namedItem(name);

    const clearError = () => {
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
      form.querySelectorAll('[aria-invalid]').forEach(el => el.removeAttribute('aria-invalid'));
    };

    const showError = (message, input) => {
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    };

    form.addEventListener('submit', event => {
      clearError();
      const nameInput = field('contact[name]');
      const emailInput = field('contact[email]');
      if (nameInput && !nameInput.value.trim()) {
        event.preventDefault();
        showError(errorEl ? errorEl.dataset.msgName : '', nameInput);
        return;
      }
      if (emailInput && !EMAIL_PATTERN.test(emailInput.value.trim())) {
        event.preventDefault();
        showError(errorEl ? errorEl.dataset.msgEmail : '', emailInput);
      }
    });

    document.querySelectorAll('[data-piece]').forEach(link => {
      link.addEventListener('click', () => {
        if (!pieceSelect) return;
        const match = [...pieceSelect.options].find(o => o.value === link.dataset.piece);
        if (match) pieceSelect.value = match.value;
      });
    });
  }

  /* ---------- Start ---------- */

  function init() {
    document.querySelectorAll('.lattice-svg').forEach(buildLattice);
    watchElements();
    wireHolds();
    wireForm();
    scheduleLayout();
    updateNav();
  }

  makeDust();
  init();
  if (reduceMotion.matches) pinToFinalStates();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', scheduleLayout, { passive: true });
  if (main && 'ResizeObserver' in window) new ResizeObserver(scheduleLayout).observe(main);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleLayout);

  // The theme editor re-renders a section in place when you change it; wire up the new markup.
  document.addEventListener('shopify:section:load', init);
})();
